import { useEffect, useRef, useState } from 'react';
import { LazyImage } from '../common/LazyImage';
import { Clock } from 'lucide-react';

export function NoMoreShadowBaseV() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isDark, setIsDark] = useState(true);

  // Detect theme change and initialize
  useEffect(() => {
    // Check for dark mode
    const checkDarkMode = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      setIsDark(isDarkMode);
    };

    // Initial check
    checkDarkMode();

    // Set up listeners for changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.attributeName === 'class' &&
          mutation.target === document.documentElement
        ) {
          checkDarkMode();
        }
      });
    });

    // Start observing document for class changes
    observer.observe(document.documentElement, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-background-light to-surface-light dark:from-background-dark dark:to-surface-dark relative overflow-hidden"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-text-primary-light dark:text-text-primary-dark bg-surface-light dark:bg-surface-dark text-sm font-medium mb-6">
            <Clock className="w-4 h-4" />
            Coming Soon
          </div>
          <h2
            className={`text-3xl md:text-4xl font-bold text-center mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark transform transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
          >
            Get Spotted - No More Practicing in Shadow
          </h2>
          <p
            className={`text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto transform transition-all duration-500 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
          >
            With <span className="text-text-primary-light dark:text-text-primary-dark font-semibold">MockTrail <span className="text-secondary-light dark:text-secondary-dark">x</span> JobProt</span>, you're not just practicing for interviews, you're becoming a potential candidate for the companies who're hiring for those job roles.
          </p>
        </div>

        <div
          className={`max-w-6xl mx-auto transform transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          style={{ transitionDelay: '0.2s' }}
        >
          <div className="group relative rounded-2xl transition-transform duration-300 ease-out hover:scale-[1.02]">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" />

            <div className="relative p-6 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark">
              <div className="overflow-hidden rounded-lg">
                <LazyImage
                  src={isDark ? "mocktrail-getspottedimg-darkui.png" : "mocktrail-getspottedimg-lightui.png"}
                  alt="Interview Practice Demonstration"
                  className="w-full transition-all duration-500 ease-in-out hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 